import { useState, useEffect, useRef } from "react";
import { Play, ChevronDown, ArrowRight, Check, Minus, Plus, Calendar, Clock, Users, FileSearch, MessageCircle, Settings, Zap, AlertTriangle, RefreshCw, Shield, TrendingUp, Hexagon, Activity, Box, Triangle, Star, ClipboardCheck, Target, BarChart3, Handshake } from "lucide-react";

/* ───────────────────────── helpers ───────────────────────── */
const useInView = (opts = {}) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.15, ...opts });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
};

const FadeIn = ({ children, className = "", delay = 0 }) => {
  const [ref, visible] = useInView();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const LOGO_URL = "https://customer-assets.emergentagent.com/job_e6aa7c2c-d03d-4974-9cb2-630ed0a0b376/artifacts/e7qtpwyy_Bazaart_2CA00B66-0594-4702-AC94-D9D6D5BB8442%20%281%29.JPEG";

/* ───────────────────────── data ───────────────────────── */
const PAIN_POINTS = [
  { icon: Zap, label: "Staff burnout", desc: "Your team is running on fumes. Morale is dropping and you can feel it every day." },
  { icon: RefreshCw, label: "Constant follow-up from leadership", desc: "You're chasing tasks that should be handled without you stepping in." },
  { icon: MessageCircle, label: "Poor communication across teams", desc: "Messages get lost, handoffs break down, and no one is on the same page." },
  { icon: Users, label: "High turnover", desc: "You keep losing good people and you're not sure if it's the culture or the structure." },
  { icon: AlertTriangle, label: "Tasks slipping through the cracks", desc: "Things that should be routine keep getting missed or delayed." },
  { icon: Settings, label: "Feeling reactive instead of organized", desc: "Every day feels like putting out fires instead of moving forward." },
  { icon: Shield, label: "Compliance stress", desc: "You're not sure if your documentation and processes would pass a real audit." },
  { icon: TrendingUp, label: "Operational confusion as you grow", desc: "What worked with a small team is now breaking down as you scale." },
];

const PROCESS_STEPS = [
  {
    num: "01",
    title: "Initial Consultation & Operational Discussion",
    desc: "We start with a focused conversation with leadership to understand what you're dealing with. We learn about your team structure, daily pain points, and what success looks like for you.",
    detail: "This isn't a sales call. It's a real conversation to determine whether we can help and where the biggest opportunities are.",
  },
  {
    num: "02",
    title: "Assessment of Workflows, Communication & Team Structure",
    desc: "We review how work actually moves through your organization. This includes interviews with key team members, documentation review, and analysis of your communication patterns and role clarity.",
    detail: "We look at workload distribution, coverage issues, handoff breakdowns, and whether the right people are in the right seats.",
  },
  {
    num: "03",
    title: "Identify Operational Gaps & Root Causes",
    desc: "We pinpoint the specific bottlenecks, unclear ownership, and process breakdowns causing friction. We separate the symptoms from the root causes so you stop fixing the wrong things.",
    detail: "You'll get a prioritized list of issues ranked by impact, not just a list of everything that's wrong.",
  },
  {
    num: "04",
    title: "Clear Recommendations & Action Plan",
    desc: "We deliver a structured findings report with an executive summary, key findings, and a practical 30-60-90 day roadmap. Every recommendation is specific, actionable, and prioritized.",
    detail: "No generic advice. You'll know exactly what to do first, second, and third.",
  },
  {
    num: "05",
    title: "Implementation Support & Follow-Through",
    desc: "We don't just hand you a report and walk away. We stay involved to help you put improvements into practice, build accountability systems, and make sure changes actually stick.",
    detail: "This is where most consulting falls apart. We make sure it doesn't.",
  },
];

const INCLUDED = [
  { icon: Calendar, label: "Leadership consultation", desc: "Direct access to our team for strategic guidance" },
  { icon: Users, label: "Team & workflow analysis", desc: "Deep dive into how your people and processes actually work" },
  { icon: FileSearch, label: "Operational review", desc: "Full assessment of your systems, structure, and gaps" },
  { icon: MessageCircle, label: "Communication analysis", desc: "Identify where messages break down between teams" },
  { icon: ClipboardCheck, label: "Clear action recommendations", desc: "A prioritized 30-60-90 day roadmap you can act on immediately" },
  { icon: Handshake, label: "Follow-up support", desc: "Ongoing guidance to make sure changes are implemented" },
];

const FAQS = [
  { q: "Is this only for large healthcare organizations?", a: "No. Most of the organizations we work with are growing teams trying to improve operations before problems get worse. Whether you have 10 employees or 200, if things feel harder than they should, we can help." },
  { q: "What if we think our issue is staffing?", a: "Sometimes it is. But more often than not, staffing problems are symptoms of workflow and communication issues underneath the surface. Our assessment will help you see the difference so you're not spending money on the wrong fix." },
  { q: "Do you help implement changes?", a: "Yes. The goal is not just identifying problems. We help create practical solutions and stay involved through implementation to make sure improvements actually stick." },
];

/* ───────────────────────── components ───────────────────────── */

const LPHeader = () => (
  <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-white/80 border-b border-[#00838A]/10" data-testid="lp-header">
    <div className="max-w-7xl mx-auto px-6 md:px-8 h-16 flex items-center justify-between">
      <a href="/consultation" className="flex items-center" data-testid="lp-logo">
        <img
          src={LOGO_URL}
          alt="RNB Consulting Group"
          className="h-12 w-auto rounded-lg"
        />
      </a>
      <a href="#book" data-testid="header-cta-button">
        <button className="bg-[#00838A] text-[#0B1F3A] font-bold text-sm px-5 py-2.5 rounded-full hover:scale-105 transition-transform">
          Book a Consultation
        </button>
      </a>
    </div>
  </header>
);

const LPFooter = () => (
  <footer className="bg-[#0B1F3A] text-white/60 text-sm py-12">
    <div className="max-w-7xl mx-auto px-6 md:px-8 flex flex-col md:flex-row items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <img src={LOGO_URL} alt="RNB Consulting Group" className="h-8 w-auto rounded" />
        <p className="font-heading font-bold text-white tracking-tight">RNB Consulting Group</p>
      </div>
      <p>&copy; {new Date().getFullYear()} RNB Consulting Group. All rights reserved.</p>
    </div>
  </footer>
);

/* ───────────────────────── page ───────────────────────── */
const ConsultationLP = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const scrollToBook = () => document.getElementById("book")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="bg-white text-[#0B1F3A] font-body overflow-x-hidden">
      <LPHeader />

      {/* ─── HERO ─── */}
      <section className="pt-28 pb-20 md:pt-36 md:pb-28 relative" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-b from-[#E8F4F5]/40 to-white pointer-events-none" />
        <div className="max-w-4xl mx-auto px-6 md:px-8 text-center relative">
          <FadeIn>
            <p className="text-sm font-accent font-semibold uppercase tracking-[0.2em] text-[#00838A] mb-6" data-testid="hero-overline">
              Healthcare Operations Consulting
            </p>
          </FadeIn>
          <FadeIn delay={100}>
            <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl font-black tracking-tighter leading-[1.05] text-[#0B1F3A] mb-6" data-testid="hero-headline">
              Your staff probably isn't lazy.<br />
              <span className="text-[#00838A]">Your systems are probably unclear.</span>
            </h1>
          </FadeIn>
          <FadeIn delay={200}>
            <p className="text-lg md:text-xl text-[#5A5A5A] leading-relaxed max-w-2xl mx-auto mb-10">
              We help healthcare leaders identify what's actually slowing their teams down so they can reduce burnout, improve accountability, and create smoother day-to-day operations.
            </p>
          </FadeIn>
          <FadeIn delay={300}>
            <button
              onClick={scrollToBook}
              className="bg-[#00838A] text-[#0B1F3A] font-bold text-base px-8 py-4 rounded-full hover:scale-105 transition-transform shadow-lg shadow-[#00838A]/25 inline-flex items-center gap-2"
              data-testid="hero-cta-button"
            >
              Book a Consultation <ArrowRight className="w-4 h-4" />
            </button>
          </FadeIn>

          {/* VSL */}
          <FadeIn delay={400}>
            <div className="mt-14 relative rounded-2xl overflow-hidden shadow-2xl ring-1 ring-[#00838A]/10 aspect-video bg-[#0B1F3A] group cursor-pointer" data-testid="vsl-section">
              <img
                src="https://images.unsplash.com/photo-1653566031535-bcf33e1c2893?w=1280&h=720&fit=crop&q=80"
                alt="Healthcare consultation"
                className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-50 transition-opacity"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center ring-2 ring-white/30 animate-pulse" data-testid="vsl-play-button">
                  <Play className="w-8 h-8 text-white fill-white ml-1" />
                </div>
              </div>
              <div className="absolute bottom-4 left-4 right-4 text-white/70 text-xs font-accent">
                Watch: Why most healthcare teams struggle with operations
              </div>
            </div>
          </FadeIn>

          <FadeIn delay={500}>
            <div className="mt-8 flex justify-center">
              <ChevronDown className="w-6 h-6 text-[#00838A]/40 animate-bounce" />
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ─── TRUST BAR ─── */}
      <section className="py-12 border-y border-[#00838A]/10 bg-[#E8F4F5]/20" data-testid="trust-bar">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <p className="text-center text-xs font-accent font-semibold uppercase tracking-[0.2em] text-[#5A5A5A]/60 mb-6">
            Trusted by healthcare organizations
          </p>
          <div className="flex items-center justify-center gap-10 md:gap-16 flex-wrap opacity-40 grayscale">
            {[Hexagon, Activity, Box, Triangle, Star, Shield].map((Icon, i) => (
              <div key={`trust-${i}`} className="flex items-center gap-2 text-[#0B1F3A]">
                <Icon className="w-8 h-8" strokeWidth={1.5} />
                <span className="text-sm font-heading font-bold tracking-tight hidden sm:block">
                  {["HealthFirst", "MedGroup", "CareNet", "PrimaCare", "VistaMed", "ComplianceIQ"][i]}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── PAIN POINTS ─── */}
      <section className="py-24 md:py-32" data-testid="pain-points-section">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <FadeIn>
            <p className="text-sm font-accent font-semibold uppercase tracking-[0.2em] text-[#00838A] mb-4">The reality</p>
            <h2 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight text-[#0B1F3A] mb-4">
              Things probably feel harder<br className="hidden md:block" /> than they should.
            </h2>
            <p className="text-lg text-[#5A5A5A] max-w-2xl mb-14">
              If any of these sound familiar, you're not alone. And it's usually not a people problem. It's a systems problem.
            </p>
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="pain-points-grid">
            {PAIN_POINTS.map((p, i) => (
              <FadeIn key={`pain-${i}`} delay={i * 80}>
                <div className="group p-6 rounded-xl border border-[#00838A]/10 bg-white hover:-translate-y-1 hover:shadow-lg transition-all cursor-default h-full" data-testid={`pain-point-${i}`}>
                  <p.icon className="w-6 h-6 text-[#00838A] mb-3 group-hover:text-[#00838A] transition-colors" />
                  <p className="font-heading font-bold text-[#0B1F3A] text-base mb-2">{p.label}</p>
                  <p className="text-sm text-[#5A5A5A] leading-relaxed">{p.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ─── WHY CHOOSE US ─── */}
      <section className="py-24 md:py-32 bg-[#0B1F3A] text-white" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <FadeIn>
              <div>
                <p className="text-sm font-accent font-semibold uppercase tracking-[0.2em] text-[#00838A] mb-4">Why RNB</p>
                <h2 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight mb-6">
                  Why healthcare organizations trust us.
                </h2>
                <p className="text-white/70 text-lg leading-relaxed mb-4">
                  Most consultants give generic advice. We focus on identifying what's actually happening operationally inside your organization so you can stop guessing and start fixing the right things.
                </p>
                <p className="text-white/70 text-base leading-relaxed mb-8">
                  We step in as a neutral third party and help uncover unclear expectations, broken workflows, communication gaps, and operational bottlenecks that your team is too close to see clearly.
                </p>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm font-accent uppercase tracking-[0.15em] text-[#00838A]/60 mb-3">The issue usually isn't</p>
                    {["Lazy staff", "Lack of effort", "Lack of caring"].map((t) => (
                      <p key={t} className="text-white/50 flex items-center gap-2 mb-2 text-sm">
                        <Minus className="w-3 h-3 text-white/30 shrink-0" />{t}
                      </p>
                    ))}
                  </div>
                  <div>
                    <p className="text-sm font-accent uppercase tracking-[0.15em] text-[#00838A] mb-3">It's usually</p>
                    {["Unclear systems", "Unclear ownership", "Unclear communication"].map((t) => (
                      <p key={t} className="text-white flex items-center gap-2 mb-2 text-sm font-medium">
                        <Check className="w-3.5 h-3.5 text-[#00838A] shrink-0" />{t}
                      </p>
                    ))}
                  </div>
                </div>
                <p className="text-white/60 text-base mt-8 border-t border-white/10 pt-6">
                  We help bring clarity to all three. Because most teams are too close to the problem to clearly see it.
                </p>
              </div>
            </FadeIn>
            <FadeIn delay={200}>
              <div className="rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10">
                <img
                  src="https://images.pexels.com/photos/5452228/pexels-photo-5452228.jpeg?auto=compress&w=800&h=600&fit=crop"
                  alt="Healthcare team collaboration"
                  className="w-full h-auto object-cover"
                />
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ─── SOCIAL PROOF ─── */}
      <section className="py-24 md:py-32" data-testid="social-proof-section">
        <div className="max-w-5xl mx-auto px-6 md:px-8">
          <FadeIn>
            <p className="text-sm font-accent font-semibold uppercase tracking-[0.2em] text-[#00838A] text-center mb-4">What they're saying</p>
            <h2 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight text-[#0B1F3A] text-center mb-16">
              Real results. Real relief.
            </h2>
          </FadeIn>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                quote: "It got to a point where I felt like I was carrying the whole team. I started questioning if I hired the wrong people. Going through this process made me realize it wasn't the team. It was how everything was set up.",
                name: "Dr. D. Vijapura",
                img: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=120&h=120&fit=crop&crop=face&q=80",
              },
              {
                quote: "Once they stepped in, it felt like a weight was lifted. We finally had a space to be honest and address what was really happening.",
                name: "Tonya Lathan",
                img: "https://images.unsplash.com/photo-1621533463370-837f20c6c889?w=120&h=120&fit=crop&crop=face&q=80",
              },
            ].map((t, i) => (
              <FadeIn key={`testimonial-${i}`} delay={i * 150}>
                <div className="p-8 rounded-2xl border border-[#00838A]/10 bg-white hover:shadow-lg transition-shadow" data-testid={`testimonial-card-${i}`}>
                  <div className="flex gap-1 text-[#C9A84C] mb-5">
                    {[...Array(5)].map((_, si) => <Star key={`star-t${i}-${si}`} className="w-4 h-4 fill-current" />)}
                  </div>
                  <p className="text-[#0B1F3A] text-base leading-relaxed italic mb-6">"{t.quote}"</p>
                  <div className="flex items-center gap-3">
                    <img src={t.img} alt={t.name} className="w-11 h-11 rounded-full object-cover ring-2 ring-[#00838A]/30" />
                    <p className="font-heading font-bold text-sm text-[#0B1F3A]">{t.name}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ─── PROCESS ─── */}
      <section className="py-24 md:py-32 bg-[#E8F4F5]/30" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.4fr] gap-16">
            <FadeIn>
              <div className="md:sticky md:top-28">
                <p className="text-sm font-accent font-semibold uppercase tracking-[0.2em] text-[#00838A] mb-4">The process</p>
                <h2 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight text-[#0B1F3A] mb-4">
                  What working together looks like.
                </h2>
                <p className="text-[#5A5A5A] text-lg leading-relaxed mb-6">
                  A clear, structured approach designed to get to the root of what's happening inside your organization. No guesswork. No generic advice.
                </p>
                <div className="hidden md:flex items-center gap-3 text-sm text-[#5A5A5A] border-t border-[#00838A]/10 pt-6">
                  <Target className="w-5 h-5 text-[#00838A]" />
                  <span>Every engagement includes a structured findings report with a prioritized action plan.</span>
                </div>
              </div>
            </FadeIn>
            <div className="space-y-6" data-testid="process-steps">
              {PROCESS_STEPS.map((s, i) => (
                <FadeIn key={`step-${i}`} delay={i * 100}>
                  <div className="relative p-6 md:p-8 rounded-xl border border-[#00838A]/10 bg-white hover:-translate-y-0.5 hover:shadow-md transition-all" data-testid={`process-step-${i}`}>
                    <span className="absolute -top-3 -left-2 text-7xl md:text-8xl font-heading font-black text-[#00838A]/[0.06] select-none leading-none pointer-events-none">
                      {s.num}
                    </span>
                    <div className="relative">
                      <p className="font-accent text-xs uppercase tracking-[0.15em] text-[#00838A] mb-1">Step {s.num}</p>
                      <h3 className="font-heading text-xl md:text-2xl font-bold text-[#0B1F3A] mb-2">{s.title}</h3>
                      <p className="text-[#5A5A5A] text-base leading-relaxed mb-3">{s.desc}</p>
                      <p className="text-sm text-[#00838A]/80 italic">{s.detail}</p>
                    </div>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── WHAT'S INCLUDED ─── */}
      <section className="py-24 md:py-32" data-testid="included-section">
        <div className="max-w-5xl mx-auto px-6 md:px-8 text-center">
          <FadeIn>
            <p className="text-sm font-accent font-semibold uppercase tracking-[0.2em] text-[#00838A] mb-4">What's included</p>
            <h2 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight text-[#0B1F3A] mb-6">
              Everything you need to move forward.
            </h2>
            <p className="text-lg text-[#5A5A5A] max-w-2xl mx-auto mb-14">
              Every engagement is designed to give you clarity, direction, and a real plan you can act on.
            </p>
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {INCLUDED.map((item, i) => (
              <FadeIn key={`incl-${i}`} delay={i * 80}>
                <div className="p-6 rounded-xl border border-[#00838A]/10 bg-white hover:-translate-y-1 hover:shadow-md transition-all text-center h-full" data-testid={`included-${i}`}>
                  <item.icon className="w-7 h-7 text-[#00838A] mx-auto mb-3" />
                  <p className="font-heading font-bold text-[#0B1F3A] text-base mb-1.5">{item.label}</p>
                  <p className="text-sm text-[#5A5A5A]">{item.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ─── SECOND CTA ─── */}
      <section className="py-24 md:py-32 bg-[#0B1F3A]" data-testid="second-cta-section">
        <div className="max-w-3xl mx-auto px-6 md:px-8 text-center">
          <FadeIn>
            <h2 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight text-white mb-6">
              If things feel off, they probably are.
            </h2>
            <p className="text-white/60 text-lg mb-10 max-w-xl mx-auto">
              Book a conversation and let's figure out what's actually happening beneath the surface.
            </p>
            <button
              onClick={scrollToBook}
              className="bg-[#00838A] text-[#0B1F3A] font-bold text-base px-8 py-4 rounded-full hover:scale-105 transition-transform shadow-lg shadow-[#00838A]/20 inline-flex items-center gap-2"
              data-testid="second-cta-button"
            >
              Book a Consultation <ArrowRight className="w-4 h-4" />
            </button>
          </FadeIn>
        </div>
      </section>

      {/* ─── ABOUT US ─── */}
      <section className="py-24 md:py-32" data-testid="about-section">
        <div className="max-w-5xl mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[auto_1fr] gap-12 items-center">
            <FadeIn>
              <img src={LOGO_URL} alt="RNB Consulting Group" className="h-24 w-auto rounded-xl mx-auto md:mx-0" />
            </FadeIn>
            <FadeIn delay={100}>
              <div>
                <p className="text-sm font-accent font-semibold uppercase tracking-[0.2em] text-[#00838A] mb-3">About us</p>
                <h2 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight text-[#0B1F3A] mb-5">
                  RNB Consulting Group
                </h2>
                <p className="text-[#5A5A5A] text-lg leading-relaxed mb-4">
                  RNB Consulting Group helps healthcare organizations improve operations, reduce internal chaos, strengthen communication, and create more sustainable systems for growth.
                </p>
                <p className="text-[#5A5A5A] text-base leading-relaxed mb-4">
                  We work with practices, clinics, and healthcare teams who know something isn't working but can't pinpoint exactly what. Our team provides structured assessments and hands-on support across workforce optimization, compliance readiness, workflow design, credentialing, technology adoption, and patient experience.
                </p>
                <p className="text-[#5A5A5A] text-base leading-relaxed">
                  Whether you need help with a specific challenge or want a partner to strengthen your entire operation, we're here. We don't just tell you what's wrong. We help you build something that actually works.
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section className="py-24 md:py-32 bg-[#E8F4F5]/30" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-6 md:px-8">
          <FadeIn>
            <p className="text-sm font-accent font-semibold uppercase tracking-[0.2em] text-[#00838A] text-center mb-4">FAQ</p>
            <h2 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight text-[#0B1F3A] text-center mb-14">
              Common questions
            </h2>
          </FadeIn>
          <div className="space-y-4" data-testid="faq-accordion">
            {FAQS.map((f, i) => {
              const isOpen = openFaq === i;
              return (
                <FadeIn key={`faq-${i}`} delay={i * 100}>
                  <div
                    className={`border rounded-xl transition-all ${isOpen ? "border-[#00838A]/20 shadow-md bg-white" : "border-[#00838A]/10 bg-white"}`}
                    data-testid={`faq-item-${i}`}
                  >
                    <button
                      onClick={() => setOpenFaq(isOpen ? null : i)}
                      className="w-full flex items-center justify-between p-5 md:p-6 text-left"
                    >
                      <span className="font-heading font-bold text-[#0B1F3A] text-base pr-4">{f.q}</span>
                      {isOpen ? <Minus className="w-5 h-5 text-[#00838A] shrink-0" /> : <Plus className="w-5 h-5 text-[#00838A] shrink-0" />}
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ${isOpen ? "max-h-60 opacity-100" : "max-h-0 opacity-0"}`}>
                      <p className="px-5 md:px-6 pb-5 md:pb-6 text-[#5A5A5A] text-base leading-relaxed">{f.a}</p>
                    </div>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── CALENDLY EMBED ─── */}
      <section id="book" className="py-24 md:py-32 bg-white" data-testid="calendly-section">
        <div className="max-w-4xl mx-auto px-6 md:px-8">
          <FadeIn>
            <div className="text-center mb-12">
              <p className="text-sm font-accent font-semibold uppercase tracking-[0.2em] text-[#00838A] mb-4">Let's talk</p>
              <h2 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight text-[#0B1F3A] mb-4">
                Book your consultation.
              </h2>
              <p className="text-[#5A5A5A] text-lg max-w-xl mx-auto">
                Let's identify what's actually creating stress, inefficiency, or breakdowns inside your team.
              </p>
            </div>
          </FadeIn>
          <FadeIn delay={150}>
            <div className="rounded-2xl overflow-hidden shadow-xl ring-1 ring-[#00838A]/10" data-testid="calendly-embed-container">
              <iframe
                src="https://calendly.com/rnb-consulting-group/free-discovery-call?hide_gdpr_banner=1"
                title="Book a Consultation"
                className="w-full border-0"
                style={{ minHeight: "700px" }}
              />
            </div>
          </FadeIn>
        </div>
      </section>

      <LPFooter />
    </div>
  );
};

export default ConsultationLP;
